export type LandingSectionId =
  | "home"
  | "portfolio"
  | "roadMap"
  | "technologies"
  | "live"
  | "contact";

export type LandingSectionDefinition = Readonly<{
  id: LandingSectionId;
  label: string;
  path: `/${string}`;
  anchor?: `#${string}`;
  order: number;
  urlSyncEligible: boolean;
}>;

export const DEFAULT_LANDING_SECTION_ID: LandingSectionId = "home";
export const SHOW_LEGACY_LIVE_SECTION = false;

export const LEGACY_LANDING_SECTIONS: readonly LandingSectionDefinition[] = [
  {
    id: "home",
    label: "Início",
    path: "/home",
    anchor: "#home",
    order: 1,
    urlSyncEligible: true,
  },
  {
    id: "portfolio",
    label: "Portfólio",
    path: "/portfolio",
    anchor: "#portfolio",
    order: 2,
    urlSyncEligible: true,
  },
  {
    id: "roadMap",
    label: "Serviços",
    path: "/servicos",
    anchor: "#servicos",
    order: 3,
    urlSyncEligible: true,
  },
  {
    id: "technologies",
    label: "Rede IA",
    path: "/technologies",
    anchor: "#technologies",
    order: 4,
    urlSyncEligible: true,
  },
  {
    id: "live",
    label: "Ao Vivo",
    path: "/live",
    anchor: "#live",
    order: 5,
    urlSyncEligible: true,
  },
  {
    id: "contact",
    label: "Contato",
    path: "/contact",
    anchor: "#contact",
    order: 6,
    urlSyncEligible: true,
  },
] as const;

export const LANDING_SECTIONS: readonly LandingSectionDefinition[] =
  SHOW_LEGACY_LIVE_SECTION
    ? LEGACY_LANDING_SECTIONS
    : LEGACY_LANDING_SECTIONS.filter((section) => section.id !== "live");

// A landing (celular e desktop) mostra só Início e Portfólio. As rotas das
// outras seções continuam reconhecidas e abrem o Início.
export const VISIBLE_LANDING_SECTION_IDS: readonly LandingSectionId[] = [
  "home",
  "portfolio",
];

export const VISIBLE_LANDING_SECTIONS: readonly LandingSectionDefinition[] =
  LANDING_SECTIONS.filter((section) =>
    VISIBLE_LANDING_SECTION_IDS.includes(section.id),
  );

const LANDING_SECTION_IDS = new Set<LandingSectionId>(
  LANDING_SECTIONS.map((section) => section.id),
);

const LANDING_PATH_TO_SECTION_ID = new Map<string, LandingSectionId>(
  LANDING_SECTIONS.map((section) => [section.path, section.id]),
);

const LANDING_SECTION_ID_TO_PATH = new Map<LandingSectionId, string>(
  LANDING_SECTIONS.map((section) => [section.id, section.path]),
);

const LANDING_SECTION_ID_TO_DEFINITION = new Map<
  LandingSectionId,
  LandingSectionDefinition
>(LANDING_SECTIONS.map((section) => [section.id, section]));

function normalizePathname(pathname: string | null | undefined): string {
  if (!pathname) {
    return "/";
  }

  const withoutHash = pathname.split("#")[0] ?? "";
  const withoutQuery = withoutHash.split("?")[0] ?? "";
  const trimmed = withoutQuery.trim();

  if (!trimmed) {
    return "/";
  }

  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  const collapsedSlashes = withLeadingSlash.replace(/\/{2,}/g, "/");

  if (collapsedSlashes !== "/" && collapsedSlashes.endsWith("/")) {
    return collapsedSlashes.slice(0, -1);
  }

  return collapsedSlashes;
}

export function normalizeLandingSectionId(
  value: string | null | undefined,
): LandingSectionId | null {
  if (!value) {
    return null;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  if (LANDING_SECTION_IDS.has(trimmedValue as LandingSectionId)) {
    return trimmedValue as LandingSectionId;
  }

  return null;
}

export function getLandingSectionDefinition(
  sectionId: LandingSectionId,
): LandingSectionDefinition {
  return (
    LANDING_SECTION_ID_TO_DEFINITION.get(sectionId) ??
    LANDING_SECTION_ID_TO_DEFINITION.get(DEFAULT_LANDING_SECTION_ID)!
  );
}

export function getLandingSectionLabel(sectionId: LandingSectionId): string {
  return getLandingSectionDefinition(sectionId).label;
}

export function getLandingOrderedSections(): readonly LandingSectionDefinition[] {
  return [...LANDING_SECTIONS].sort((left, right) => left.order - right.order);
}

export function getLandingOrderedSectionIds(): readonly LandingSectionId[] {
  return getLandingOrderedSections().map((section) => section.id);
}

export function getLandingSectionsByIds(
  sectionIds: readonly LandingSectionId[],
): readonly LandingSectionDefinition[] {
  const allowedIds = new Set(sectionIds);

  return getLandingOrderedSections().filter((section) =>
    allowedIds.has(section.id),
  );
}

export function getLandingUrlSyncEligibleSectionIds(): readonly LandingSectionId[] {
  return getLandingOrderedSections()
    .filter((section) => section.urlSyncEligible)
    .map((section) => section.id);
}

// Idiomas com endereço próprio para o Google e as IAs: português sem prefixo
// (/home, /portfolio), inglês sob /en e espanhol (América Latina) sob /es.
export type LandingLanguage = "pt" | "en" | "es";
export const LANDING_LANGUAGES: readonly LandingLanguage[] = ["en", "pt", "es"];
export const LANGUAGE_PATH_PREFIX: Readonly<Record<LandingLanguage, string>> = {
  pt: "",
  en: "/en",
  es: "/es",
};
const LANGUAGE_PATH_PATTERN = /^\/(en|es)(?=\/|$)/;

export function getLanguageFromPath(
  pathname: string | null | undefined,
): LandingLanguage {
  const match = normalizePathname(pathname).match(LANGUAGE_PATH_PATTERN);
  return (match?.[1] as LandingLanguage | undefined) ?? "pt";
}

export function getCurrentLanguagePathPrefix(): string {
  const pathname = globalThis.window?.location.pathname;
  return LANGUAGE_PATH_PREFIX[getLanguageFromPath(pathname)];
}

export function stripLanguagePrefix(pathname: string | null | undefined): string {
  return normalizePathname(normalizePathname(pathname).replace(LANGUAGE_PATH_PATTERN, ""));
}

/** Mesmo endereço em outro idioma (a raiz vira o Início). */
export function getLanguagePath(
  language: LandingLanguage,
  pathname: string | null | undefined,
): string {
  const sectionPath = stripLanguagePrefix(pathname);
  return `${LANGUAGE_PATH_PREFIX[language]}${sectionPath === "/" ? "/home" : sectionPath}/`;
}

export function getPathBySectionId(
  sectionId: LandingSectionId,
  fallbackSectionId: LandingSectionId = DEFAULT_LANDING_SECTION_ID,
): string {
  const path =
    LANDING_SECTION_ID_TO_PATH.get(sectionId) ??
    LANDING_SECTION_ID_TO_PATH.get(fallbackSectionId) ??
    "/home";

  return `${getCurrentLanguagePathPrefix()}${path}`;
}

export function getSectionIdByPath(
  pathname: string | null | undefined,
): LandingSectionId | null {
  const normalizedPathname = stripLanguagePrefix(pathname);

  if (normalizedPathname === "/") {
    return DEFAULT_LANDING_SECTION_ID;
  }

  return LANDING_PATH_TO_SECTION_ID.get(normalizedPathname) ?? null;
}

export function isLandingPath(pathname: string | null | undefined): boolean {
  return getSectionIdByPath(pathname) !== null;
}
